import { useEffect } from 'react';
import _get from 'lodash/get';
import { FetchResult } from '@apollo/client';
import {
  DashboardTasksQueryVariables,
  Task,
  TaskFilterInputObject,
  TaskScopeCustomInputObject,
  useDashboardTaskIdsLazyQuery,
  useFormTemplateTasksQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import { TaskCounter } from '@lib/interfaces/task';
import getTaskCounterData from '@lib/utils/getTaskCounterData';
import { ListQueryHookProps } from '../types';

export type FormTemplateTasksQueryHookResult = {
  fetchTaskIds: () => ReturnType<
    ReturnType<typeof useDashboardTaskIdsLazyQuery>[0]
  >;
  refetchTasks: () => Promise<FetchResult<unknown>>;
  taskCounterData: TaskCounter | undefined;
  tasks: Task[];
  tasksError: Error | undefined;
  tasksFirstLoading: boolean;
  tasksLoading: boolean;
  totalPages: number;
  totalTasksCount: number;
};

export default function useFormTemplateTasksQueryHook({
  customScope,
  paginationParams,
  searchQuery,
  skip,
  onCompleted,
}: ListQueryHookProps & {
  customScope?: TaskScopeCustomInputObject;
}): FormTemplateTasksQueryHookResult {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const tasksQueryVariables: DashboardTasksQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    scope: { custom: customScope },
  };

  const filters: TaskFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'formCategoryName' },
        { fieldName: 'siteName' },
        { fieldName: 'siteAreaName' },
        { fieldName: 'siteCode' },
        { fieldName: 'siteAreaCode' },
        { fieldName: 'priority', predicateType: FilterSchemaPredicateType.Eq },
      ]),
    );
  }
  if (filters.length) {
    tasksQueryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    tasksQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const tasksQuery = useFormTemplateTasksQuery({
    fetchPolicy: 'no-cache',
    variables: tasksQueryVariables,
    skip,
  });
  const [fetchTaskIdsQuery] = useDashboardTaskIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchTaskIds = () =>
    fetchTaskIdsQuery({
      variables: {
        onlyIds: true,
        filters: tasksQueryVariables.filters,
        scope: tasksQueryVariables.scope,
      },
    });
  const {
    loading: tasksLoading,
    data,
    previousData,
    refetch: refetchTasks,
    error: tasksError,
  } = tasksQuery;

  useEffect(() => {
    if (data && onCompleted) onCompleted();
  }, [data, onCompleted]);

  const queryData = data || previousData;
  const { tasks, ...taskCounterData } = queryData || {};
  const tasksFirstLoading = tasksLoading && previousData === undefined;
  const metadata = _get(queryData, 'tasks.metadata');
  const totalTasksCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    fetchTaskIds,
    refetchTasks,
    taskCounterData: getTaskCounterData(taskCounterData),
    tasks: _get(tasks, 'collection', []) as Task[],
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalPages,
    totalTasksCount,
  };
}
